import React, { useState, useMemo } from 'react';
import {
  X,
  RefreshCw,
  Database,
  CloudDownload,
  CheckCircle2,
  AlertCircle,
  HardDrive,
  ShieldCheck,
  Layers,
  Search,
  ExternalLink,
  Plus,
  Trash2,
  Sparkles,
  Package,
} from 'lucide-react';
import { Product } from '../types';
import { db, storage } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';

interface ProductSyncModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductSynced: (product: Product) => void;
  syncedProducts: Product[];
  onRemoveSyncedProduct: (productId: string) => void;
}

const POPULAR_GIFFARINE_CODES = [
  { code: '84032', name: 'ไฮยา ไวท์เทนนิ่ง พรี-ซีรั่ม' },
  { code: '84054', name: 'พรีเมียม เอชพีอาร์ เรตินอยด์' },
  { code: '82023', name: 'เลซิติน 60 เม็ด' },
  { code: '82036', name: 'นูทริ โฟลิค' },
  { code: '40111', name: 'น้ำมันปลา 4X (90 แคปซูล)' },
  { code: '41218', name: 'รอยัลคราวน์ อเมริกาโน่' },
  { code: '10101', name: 'ครีมกันแดด SPF 30' },
  { code: '10112', name: 'รีแพรี่สเนลซันสกรีน' },
];

export const ProductSyncModal: React.FC<ProductSyncModalProps> = ({
  isOpen,
  onClose,
  onProductSynced,
  syncedProducts,
  onRemoveSyncedProduct,
}) => {
  const [activeTab, setActiveTab] = useState<'all' | 'single'>('all');
  const [productCodeInput, setProductCodeInput] = useState('');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [previewProduct, setPreviewProduct] = useState<Product | null>(null);
  const [isSavingToFirebase, setIsSavingToFirebase] = useState(false);

  // Filter catalog in modal
  const filteredCatalog = useMemo(() => {
    if (!catalogSearch.trim()) return syncedProducts.slice(0, 50);
    const q = catalogSearch.toLowerCase().trim();
    return syncedProducts.filter(
      (p) => p.name.toLowerCase().includes(q) || p.code.includes(q) || p.category.toLowerCase().includes(q)
    );
  }, [syncedProducts, catalogSearch]);

  if (!isOpen) return null;

  // Single code sync
  const handleSyncProduct = async (codeToSync?: string) => {
    const code = (codeToSync || productCodeInput).trim();
    if (!code) {
      setStatusMessage({ type: 'error', text: 'กรุณากรอกรหัสสินค้ากิฟฟารีน (เช่น 84032, 84054)' });
      return;
    }

    try {
      setLoading(true);
      setStatusMessage({ type: 'info', text: `กำลังดึงข้อมูลรหัส ${code} จากระบบกิฟฟารีนและดาวน์โหลดภาพแคช...` });

      const res = await fetch('/api/sync-giffarine-product', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productCode: code }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'ไม่สามารถดึงข้อมูลได้');
      }

      setPreviewProduct(data.product);
      setStatusMessage({
        type: 'success',
        text: `ดึงข้อมูลรหัส ${code} สำเร็จ! ภาพสินค้าถูกแคชและฝังลงในระบบเรียบร้อย`,
      });
    } catch (err: any) {
      console.error('Sync error:', err);
      setStatusMessage({
        type: 'error',
        text: err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูลสินค้า',
      });
    } finally {
      setLoading(false);
    }
  };

  // Batch sync all new products from https://www.giffarine.com/products?sortby=new
  const handleSyncAllNew = async () => {
    try {
      setIsSyncingAll(true);
      setStatusMessage({
        type: 'info',
        text: 'กำลังเชื่อมต่อและดึงสินค้าทั้งหมดจาก https://www.giffarine.com/products?sortby=new ...',
      });

      const res = await fetch('/api/sync-all-new-products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ maxPages: 25 }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'เกิดข้อผิดพลาดในการดึงข้อมูลสินค้าทั้งหมด');
      }

      setStatusMessage({
        type: 'success',
        text: `ดึงสินค้าจาก giffarine.com สำเร็จเรียบร้อย! ข้อมูลสินค้าถูกอัปเดตเข้าระบบทั้งสิ้น ${data.grandTotal || syncedProducts.length} รายการ`,
      });

      // Trigger refetch
      window.location.reload();
    } catch (err: any) {
      console.error('Batch sync error:', err);
      setStatusMessage({
        type: 'error',
        text: err.message || 'ไม่สามารถดึงสินค้าทั้งหมดได้ กรุณาลองใหม่อีกครั้ง',
      });
    } finally {
      setIsSyncingAll(false);
    }
  };

  const handleConfirmSave = async () => {
    if (!previewProduct) return;

    try {
      setIsSavingToFirebase(true);
      setStatusMessage({ type: 'info', text: 'กำลังบันทึกลงเว็บและเชื่อมโยง Firestore / Storage...' });

      try {
        const productRef = doc(db, 'products', previewProduct.id);
        await setDoc(
          productRef,
          {
            ...previewProduct,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        );
      } catch (fbErr) {
        console.warn('Firestore backup note:', fbErr);
      }

      onProductSynced(previewProduct);

      setStatusMessage({
        type: 'success',
        text: `เพิ่ม "${previewProduct.name}" เข้าสู่หน้าร้านเรียบร้อยแล้ว`,
      });

      setTimeout(() => {
        setPreviewProduct(null);
        setProductCodeInput('');
      }, 1200);
    } catch (err: any) {
      console.error(err);
      setStatusMessage({ type: 'error', text: 'ไม่สามารถบันทึกข้อมูลได้: ' + err.message });
    } finally {
      setIsSavingToFirebase(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fadeIn overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full border border-slate-200 overflow-hidden my-6">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-[#0E2C58] via-[#1E40AF] to-[#3B82F6] text-white p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/20">
              <CloudDownload className="w-5 h-5 text-amber-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold">ระบบดึงข้อมูลสินค้ากิฟฟารีน (Giffarine Product Sync)</h2>
              <p className="text-xs text-blue-100 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                ดึงสินค้าทั้งหมดจาก www.giffarine.com/products?sortby=new
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 bg-slate-50 px-6 pt-3 gap-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`pb-3 px-3 text-xs font-bold border-b-2 flex items-center gap-2 transition-all ${
              activeTab === 'all'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Package className="w-4 h-4" />
            <span>สินค้าทั้งหมด ({syncedProducts.length} รายการ)</span>
          </button>
          <button
            onClick={() => setActiveTab('single')}
            className={`pb-3 px-3 text-xs font-bold border-b-2 flex items-center gap-2 transition-all ${
              activeTab === 'single'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Plus className="w-4 h-4" />
            <span>ดึงระบุรายรหัสสินค้า</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-5">
          {/* Status Message */}
          {statusMessage && (
            <div
              className={`p-3 rounded-xl text-xs flex items-start gap-2.5 ${
                statusMessage.type === 'success'
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                  : statusMessage.type === 'error'
                  ? 'bg-rose-50 text-rose-800 border border-rose-200'
                  : 'bg-blue-50 text-blue-800 border border-blue-200'
              }`}
            >
              {statusMessage.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              ) : statusMessage.type === 'error' ? (
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              ) : (
                <RefreshCw className="w-4 h-4 text-blue-600 animate-spin shrink-0 mt-0.5" />
              )}
              <p className="flex-1 leading-relaxed">{statusMessage.text}</p>
            </div>
          )}

          {activeTab === 'all' && (
            <div className="space-y-4">
              {/* Sync Summary Card */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50/40 border border-blue-100 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    <span>คลังข้อมูลสินค้ากิฟฟารีน (giffarine.com/products?sortby=new)</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    เชื่อมโยงสินค้าทั้งหมด <strong>{syncedProducts.length} รายการ</strong> พร้อมรูปภาพแท้, รหัสสินค้า, คะแนน PV และราคาสมาชิก
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleSyncAllNew}
                  disabled={isSyncingAll}
                  className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:bg-slate-300 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-xs transition-colors shrink-0"
                >
                  {isSyncingAll ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <CloudDownload className="w-3.5 h-3.5" />
                  )}
                  <span>{isSyncingAll ? 'กำลังซิงค์...' : 'อัปเดตสินค้าทั้งหมด'}</span>
                </button>
              </div>

              {/* Search catalog */}
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={catalogSearch}
                  onChange={(e) => setCatalogSearch(e.target.value)}
                  placeholder="ค้นหาในรายการสินค้าที่ดึงมา (ชื่อ, รหัส เช่น 84032, หมวดหมู่)..."
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs focus:bg-white focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* List of products */}
              <div className="max-h-72 overflow-y-auto space-y-2 pr-1 border border-slate-200 rounded-xl p-2 bg-white">
                {filteredCatalog.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-xs">
                    ไม่พบสินค้าที่ตรงกับการค้นหา
                  </div>
                ) : (
                  filteredCatalog.map((prod) => (
                    <div
                      key={prod.id || prod.code}
                      className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-slate-100 text-xs transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={prod.image}
                          alt={prod.name}
                          className="w-10 h-10 rounded-lg object-cover bg-white border border-slate-200 shrink-0"
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                        <div className="min-w-0">
                          <div className="font-bold text-slate-800 truncate">{prod.name}</div>
                          <div className="text-[11px] text-slate-500 flex items-center gap-2">
                            <span>รหัส <strong>{prod.code}</strong></span>
                            <span>•</span>
                            <span className="text-blue-700 font-semibold">฿{prod.memberPrice}</span>
                            <span>•</span>
                            <span className="text-emerald-600 font-semibold">{prod.pv} PV</span>
                            <span className="px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded text-[10px]">
                              {prod.category}
                            </span>
                          </div>
                        </div>
                      </div>
                      <a
                        href={`https://www.giffarine.com/products?id=${prod.code}`}
                        target="_blank"
                        rel="noreferrer"
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors ml-2 shrink-0"
                        title="ดูบนเว็บทางการ Giffarine"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  ))
                )}
              </div>
              <div className="text-[11px] text-slate-400 text-right">
                แสดง {filteredCatalog.length} จากทั้งหมด {syncedProducts.length} รายการ
              </div>
            </div>
          )}

          {activeTab === 'single' && (
            <div className="space-y-4">
              {/* Quick Input Bar */}
              <div className="space-y-2">
                <label className="block text-xs font-bold text-slate-700">
                  กรอกรหัสสินค้ากิฟฟารีนที่ต้องการดึงข้อมูล
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={productCodeInput}
                      onChange={(e) => setProductCodeInput(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSyncProduct()}
                      placeholder="เช่น 84032, 84054, 82023, 40111..."
                      className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-sm focus:bg-white focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleSyncProduct()}
                    disabled={loading}
                    className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:bg-slate-300 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-xs transition-colors shrink-0"
                  >
                    {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CloudDownload className="w-3.5 h-3.5" />}
                    <span>ดึงข้อมูล</span>
                  </button>
                </div>

                {/* Popular Quick-Select Badges */}
                <div className="flex flex-wrap items-center gap-1.5 pt-1">
                  <span className="text-[11px] text-slate-400">รหัสยอดนิยม:</span>
                  {POPULAR_GIFFARINE_CODES.map((item) => (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => {
                        setProductCodeInput(item.code);
                        handleSyncProduct(item.code);
                      }}
                      className="px-2 py-0.5 bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-600 rounded-md text-[11px] font-medium transition-colors border border-slate-200"
                    >
                      {item.code} ({item.name})
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview Card If Fetched */}
              {previewProduct && (
                <div className="bg-slate-50 border-2 border-blue-200 rounded-xl p-4 space-y-4">
                  <div className="text-xs font-bold text-blue-900 flex items-center justify-between">
                    <span className="flex items-center gap-1.5">
                      <Database className="w-4 h-4 text-blue-600" />
                      พรีวิวสินค้าที่ดึงข้อมูลสำเร็จ
                    </span>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded-full text-[10px] font-bold">
                      ภาพแคชเรียบร้อย
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start bg-white p-3 rounded-lg border border-slate-200">
                    <div className="w-24 h-24 rounded-lg bg-slate-100 overflow-hidden shrink-0 border border-slate-200 flex items-center justify-center relative">
                      <img
                        src={previewProduct.image}
                        alt={previewProduct.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-1 space-y-1.5 text-xs text-slate-700 w-full">
                      <div className="font-bold text-slate-900 text-sm">{previewProduct.name}</div>
                      <div className="flex flex-wrap gap-2 text-slate-500 text-[11px]">
                        <span>รหัส: <strong className="text-slate-800">{previewProduct.code}</strong></span>
                        <span>•</span>
                        <span>หมวดหมู่: <strong className="text-slate-800">{previewProduct.category}</strong></span>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        <div>
                          <span className="text-slate-400 text-[10px] block">ราคาเต็ม</span>
                          <span className="font-bold text-slate-600">฿{previewProduct.price}</span>
                        </div>
                        <div>
                          <span className="text-blue-500 text-[10px] block font-semibold">ราคาสมาชิก</span>
                          <span className="font-bold text-blue-700 text-sm">฿{previewProduct.memberPrice}</span>
                        </div>
                        <div>
                          <span className="text-emerald-500 text-[10px] block font-semibold">คะแนน PV</span>
                          <span className="font-bold text-emerald-700 text-sm">{previewProduct.pv} PV</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setPreviewProduct(null)}
                      className="px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-200 rounded-lg transition-colors"
                    >
                      ยกเลิก
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmSave}
                      disabled={isSavingToFirebase}
                      className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-lg text-xs font-bold shadow-xs transition-colors flex items-center gap-1.5"
                    >
                      {isSavingToFirebase ? (
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      )}
                      <span>บันทึกลงหน้าร้าน</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="bg-slate-50 p-4 border-t border-slate-200 flex items-center justify-between text-xs">
          <div className="text-slate-500 text-[11px] flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
            ซิงค์คลังสินค้าจาก giffarine.com โดยตรง
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl font-semibold transition-colors"
          >
            ปิดหน้าต่าง
          </button>
        </div>
      </div>
    </div>
  );
};
